import { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import {
  INVOICE_STATUS_REVERSE_MAP,
  TurnOverInvoiceListItem,
  TurnOverInvoiceStatus,
  TurnOverInvoiceValidationStatus,
  TurnoverContributionRaw,
  TurnoverInvoiceRaw,
  VALIDATION_STATUS_MAP,
  getWorkspaceId,
  useAppSelector,
  useLazyGetTurnoverInvoicesContributionQuery,
  useLazyGetTurnoverInvoicesListQuery,
  useLazyGetTurnoverInvoicesBuyersQuery,
  getAppliedFilters,
  FilterRoute,
  ListFilterKey,
  VALIDATION_STATUS_REVERSE_MAP,
  dispatch,
  updateFiltersByRoute,
} from '@zonofi/zono-money-store';
import { usePaginatedList } from './use-paginated-list';
import { useUrlDateRange } from './use-url-date-range';
import { useDebounceSearch } from './use-debounce-search';

const PAGE_SIZE = 20;

const ALL_STATUSES: TurnOverInvoiceStatus[] = [
  'Pending',
  'Approved',
  'Rejected',
];

const KNOWN_VALIDATION_DISPLAY_NAMES: TurnOverInvoiceValidationStatus[] = [
  'Parsing the invoice',
  'Parsing Failed',
  'Distributor Not Found',
  'Buyer Not Found',
  'Invoice Data Incomplete',
  'Duplicate Invoice',
  'Products Unmapped',
  'Partially Mapped',
  'Successful',
  'Likely Fraudulent',
];

const FILTER_VALIDATION_OPTIONS: TurnOverInvoiceValidationStatus[] = [
  'Distributor Not Found',
  'Buyer Not Found',
  'Duplicate Invoice',
  'Successful',
  'Likely Fraudulent',
];

function mapOneValidationStatus(
  raw: string | null | undefined
): TurnOverInvoiceValidationStatus | null {
  if (!raw) return null;
  if (VALIDATION_STATUS_MAP[raw]) return VALIDATION_STATUS_MAP[raw];
  if (
    KNOWN_VALIDATION_DISPLAY_NAMES.includes(
      raw as TurnOverInvoiceValidationStatus
    )
  ) {
    return raw as TurnOverInvoiceValidationStatus;
  }
  return null;
}

function transformBaseInvoice(
  inv: TurnoverInvoiceRaw
): Omit<TurnOverInvoiceListItem, 'contributionSkus' | 'contributionAmount'> {
  const skuCount = inv.turnoverinvoice_lines_aggregate?.aggregate?.count ?? 0;
  const invoiceAmount =
    typeof inv.invoice_amount === 'number'
      ? inv.invoice_amount
      : Number(inv.invoice_amount);

  const rawStatuses = Array.isArray(inv.invoice_validation_status)
    ? inv.invoice_validation_status.map((s) => s.validation_status)
    : [];
  const validationStatusesDisplay = rawStatuses
    .map(mapOneValidationStatus)
    .filter((s): s is TurnOverInvoiceValidationStatus => s != null);
  const validationStatusesUnique = [
    ...new Set(validationStatusesDisplay),
  ] as TurnOverInvoiceValidationStatus[];

  let validationStatusDisplay: TurnOverInvoiceValidationStatus | null =
    validationStatusesUnique[0] ?? null;
  if (validationStatusDisplay == null && inv.validationStatus) {
    validationStatusDisplay = mapOneValidationStatus(inv.validationStatus);
  }

  const STATUS_DISPLAY_MAP: Record<string, TurnOverInvoiceStatus> = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };
  const statusDisplay: TurnOverInvoiceStatus =
    STATUS_DISPLAY_MAP[inv.status] ??
    (ALL_STATUSES.includes(inv.status as TurnOverInvoiceStatus)
      ? (inv.status as TurnOverInvoiceStatus)
      : 'Pending');

  return {
    id: inv.id,
    invoiceNumber: inv.invoice_number ?? '-',
    invoiceDate: inv.invoice_date ? formatDateStr(inv.invoice_date) : '-',
    submittedByName: (inv.submittedby || inv.sellername) ?? '-',
    submittedOn: inv.created_at ? formatDateStr(inv.created_at) : '-',
    status: statusDisplay,
    validationStatus: validationStatusDisplay,
    validationStatuses: validationStatusesUnique,
    distributorName:
      inv.turnoverinvoice_detail?.matched_distributor_name ??
      inv.turnoverinvoice_detail?.distributordetails?.name ??
      inv.sellername ??
      '-',
    distributorContactName: inv.submittedby ?? '-',
    buyerName:
      inv.turnoverinvoice_detail?.matched_buyer_name ??
      inv.turnoverinvoice_detail?.buyerdetails?.name ??
      inv.buyername ??
      '-',
    invoiceSkus: skuCount,
    invoiceAmount: Number.isFinite(invoiceAmount) ? invoiceAmount : 0,
    taxableValue: inv.taxableValue ?? null,
    gst_percentage: inv.gst_percentage ?? null,
    gst_amount: inv.gst_amount ?? null,
    discount: inv.discount ?? null,
    secondary_discount: inv.secondary_discount ?? null,
    cumulative_discount: inv.cumulative_discount ?? null,
    discount_amount: inv.discount_amount ?? null,
    cess: inv.cess ?? null,
    approved_rejected_by: inv.approved_rejected_by ?? null,
    remark: inv.remark ?? null,
  };
}

function mergeContribution(
  base: Omit<
    TurnOverInvoiceListItem,
    'contributionSkus' | 'contributionAmount'
  >,
  contribData: TurnoverContributionRaw | undefined
): TurnOverInvoiceListItem {
  const sumAgg = contribData?.turnoverinvoice_lines_aggregate?.aggregate?.sum;
  const contributionAmount = (() => {
    if (!sumAgg) return null;
    const contrib = sumAgg.contribution;
    return contrib != null
      ? Number(contrib)
      : Number(sumAgg.total_amount_with_tax) ||
          Number(sumAgg.total_amount) ||
          null;
  })();

  return {
    ...base,
    contributionSkus:
      contribData?.turnoverinvoice_lines_aggregate?.aggregate?.count ?? null,
    contributionAmount,
  };
}

function formatDateStr(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
}

export const useTurnoverInvoicesList = () => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const { debouncedSearchValue } = useDebounceSearch();

  const { dateRangeFromUrl: appliedDateRange } = useUrlDateRange({
    disableDefaultDateRange: true,
  });
  const appliedFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Turnover)
  );

  const selectedValidationStatuses: string[] =
    appliedFilters?.listFilter?.[ListFilterKey.ValidationStatus] ?? [];
  const selectedBuyers: string[] =
    appliedFilters?.listFilter?.[ListFilterKey.Buyer] ?? [];
  const selectedDistributors: string[] =
    appliedFilters?.listFilter?.[ListFilterKey.Distributor] ?? [];

  const [fetchInvoices, invoicesResult] = useLazyGetTurnoverInvoicesListQuery();
  const [fetchContribution, contributionResult] =
    useLazyGetTurnoverInvoicesContributionQuery();
  const [fetchBuyerOptions, buyerOptionsResult] =
    useLazyGetTurnoverInvoicesBuyersQuery();
  const [fetchDistributorOptions, distributorOptionsResult] =
    useLazyGetTurnoverInvoicesBuyersQuery();

  const totalCount =
    invoicesResult.currentData?.turnoverinvoice_aggregate?.aggregate?.count ??
    0;
  const fetchedCount = invoicesResult.currentData?.turnoverinvoice?.length ?? 0;
  const currentOffset = invoicesResult.originalArgs?.offset ?? 0;
  const endRecord = Math.min(currentOffset + fetchedCount, totalCount);

  const buildWhereClause = useCallback(() => {
    const dbStatuses = ALL_STATUSES.map((s) => INVOICE_STATUS_REVERSE_MAP[s]);
    const where: Record<string, unknown> = {
      workspaceId: { _eq: workspaceId },
      status: { _in: [INVOICE_STATUS_REVERSE_MAP.Pending] },
    };

    // Search
    if (debouncedSearchValue && debouncedSearchValue.trim()) {
      const term = debouncedSearchValue.trim();
      where._and = [
        { status: { _in: dbStatuses } },
        {
          _or: [
            { invoice_number: { _ilike: `%${term}%` } },
            { buyername: { _ilike: `%${term}%` } },
            { sellername: { _ilike: `%${term}%` } },
            {
              turnoverinvoice_detail: {
                matched_buyer_name: { _ilike: `%${term}%` },
              },
            },
            {
              turnoverinvoice_detail: {
                matched_distributor_name: { _ilike: `%${term}%` },
              },
            },
          ],
        },
      ];
      delete where.status;
    }

    // Date filter — only applied when the user has explicitly selected a range.
    // The web app sends NO date filter by default, so we match that behaviour.
    // useUrlDateRange returns null/undefined when no date is selected.
    if (appliedDateRange?.startDate && appliedDateRange?.endDate) {
      const dateCondition = {
        invoice_date: {
          _gte: moment(appliedDateRange.startDate).startOf('day').format(),
          _lte: moment(appliedDateRange.endDate).endOf('day').format(),
        },
      };
      if (Array.isArray(where._and)) {
        (where._and as unknown[]).push(dateCondition);
      } else {
        Object.assign(where, dateCondition);
      }
    }

    // Validation status filter
    if (selectedValidationStatuses.length > 0) {
      const enumValues = selectedValidationStatuses
        .map(
          (s) =>
            VALIDATION_STATUS_REVERSE_MAP[s as TurnOverInvoiceValidationStatus]
        )
        .filter((v) => v && v !== '');

      if (enumValues.length > 0) {
        const isSuccessfulSelected =
          selectedValidationStatuses.includes('Successful');
        let validationCondition: Record<string, unknown>;

        if (isSuccessfulSelected && enumValues.length === 1) {
          validationCondition = {
            _or: [
              {
                invoice_validation_status: {
                  validation_status: { _in: enumValues },
                },
              },
              { _not: { invoice_validation_status: {} } },
            ],
          };
        } else if (isSuccessfulSelected && enumValues.length > 1) {
          const otherEnumValues = enumValues.filter((v) => v !== 'SUCCESSFUL');
          validationCondition = {
            _or: [
              {
                invoice_validation_status: {
                  validation_status: { _in: otherEnumValues },
                },
              },
              { _not: { invoice_validation_status: {} } },
            ],
          };
        } else {
          validationCondition = {
            invoice_validation_status: {
              validation_status: { _in: enumValues },
            },
          };
        }

        if (Array.isArray(where._and)) {
          (where._and as unknown[]).push(validationCondition);
        } else {
          Object.assign(where, validationCondition);
        }
      }
    }

    // Buyer filter
    if (selectedBuyers.length > 0) {
      const buyerCondition = {
        _or: [
          { buyername: { _in: selectedBuyers } },
          {
            turnoverinvoice_detail: {
              matched_buyer_name: { _in: selectedBuyers },
            },
          },
        ],
      };
      if (Array.isArray(where._and)) {
        (where._and as unknown[]).push(buyerCondition);
      } else {
        Object.assign(where, buyerCondition);
      }
    }

    // Distributor filter
    if (selectedDistributors.length > 0) {
      const distributorCondition = {
        _or: [
          { sellername: { _in: selectedDistributors } },
          {
            turnoverinvoice_detail: {
              matched_distributor_name: { _in: selectedDistributors },
            },
          },
        ],
      };
      if (Array.isArray(where._and)) {
        (where._and as unknown[]).push(distributorCondition);
      } else {
        Object.assign(where, distributorCondition);
      }
    }

    return where;
  }, [
    workspaceId,
    debouncedSearchValue,
    appliedDateRange?.startDate,
    appliedDateRange?.endDate,
    selectedValidationStatuses,
    selectedBuyers,
    selectedDistributors,
  ]);

  const buildOptionsWhereClause = useCallback(() => {
    const dbStatuses = ALL_STATUSES.map((s) => INVOICE_STATUS_REVERSE_MAP[s]);
    return {
      workspaceId: { _eq: workspaceId },
      status: { _in: dbStatuses },
    };
  }, [workspaceId]);

  const buildBuyersWhereClause = useCallback(() => {
    const dbStatuses = ALL_STATUSES.map((s) => INVOICE_STATUS_REVERSE_MAP[s]);
    return {
      workspaceId: { _eq: workspaceId },
      status: { _in: dbStatuses },
    };
  }, [workspaceId]);

  const fetchDataApiCall = useCallback(
    ({ pageNo, pageSize }: { pageNo: number; pageSize: number }) => {
      if (!workspaceId) return;

      const where = buildWhereClause();
      const offset = (pageNo - 1) * pageSize;

      fetchInvoices({
        sellerWorkspaceId: workspaceId,
        where,
        limit: pageSize,
        offset,
      });

      fetchContribution({
        sellerWorkspaceId: workspaceId,
        where,
        limit: pageSize,
        offset,
      });
    },
    [workspaceId, buildWhereClause, fetchInvoices, fetchContribution]
  );

  const contributionMap = useMemo(() => {
    const map = new Map<string, TurnoverContributionRaw>();
    contributionResult.data?.turnoverinvoice?.forEach((inv) => {
      map.set(inv.id, inv);
    });
    return map;
  }, [contributionResult.data]);

  const currentPageInvoices = useMemo<TurnOverInvoiceListItem[]>(() => {
    const raw = invoicesResult.data?.turnoverinvoice ?? [];
    return raw.map((inv) =>
      mergeContribution(transformBaseInvoice(inv), contributionMap.get(inv.id))
    );
  }, [invoicesResult.data, contributionMap]);

  const {
    data: invoicesList,
    pageNo,
    lastItemRef,
    onScroll,
    isPageLoading,
    isEndOfList,
  } = usePaginatedList<TurnOverInvoiceListItem>({
    fetchDataApiCall,
    apiResponseData: currentPageInvoices,
    isLoading: invoicesResult.isLoading || invoicesResult.isFetching,
    isError: invoicesResult.isError,
    uniqueKey: 'id',
    dependencies: [
      debouncedSearchValue,
      appliedDateRange?.startDate?.toString(),
      appliedDateRange?.endDate?.toString(),
      selectedValidationStatuses.join(','),
      selectedBuyers.join(','),
      selectedDistributors.join(','),
    ],
    pageSize: PAGE_SIZE,
    paginationMeta: {
      startRecord: 1,
      endRecord,
      totalCount,
    },
  });

  useEffect(() => {
    dispatch(
      updateFiltersByRoute({
        route: FilterRoute.Turnover,
        filter: {
          listFilter: {
            [ListFilterKey.Distributor]: {
              multiSelect: true,
              values: [],
            },
            [ListFilterKey.Buyer]: {
              multiSelect: true,
              values: [],
            },
            [ListFilterKey.ValidationStatus]: {
              multiSelect: true,
              values: FILTER_VALIDATION_OPTIONS.map((status) => ({
                id: status,
                label: status,
                selected: false,
              })),
            },
          },
        },
      })
    );
  }, []);

  useEffect(() => {
    if (!workspaceId) return;

    const where = buildOptionsWhereClause();

    fetchBuyerOptions({
      sellerWorkspaceId: workspaceId,
      where,
      limit: 5000,
      offset: 0,
    });

    fetchDistributorOptions({
      sellerWorkspaceId: workspaceId,
      where,
      limit: 5000,
      offset: 0,
    });
  }, [
    workspaceId,
    buildOptionsWhereClause,
    fetchBuyerOptions,
    fetchDistributorOptions,
  ]);

  useEffect(() => {
    const rawBuyers = buyerOptionsResult.data?.turnoverinvoice;
    if (!rawBuyers) return;

    const uniqueBuyers = [
      ...new Set(
        rawBuyers
          .map(
            (inv) =>
              inv.turnoverinvoice_detail?.matched_buyer_name ?? inv.buyername
          )
          .filter((name): name is string => Boolean(name))
      ),
    ].sort();

    dispatch(
      updateFiltersByRoute({
        route: FilterRoute.Turnover,
        filter: {
          listFilter: {
            [ListFilterKey.Buyer]: {
              multiSelect: true,
              values: uniqueBuyers.map((buyer) => ({
                id: buyer,
                label: buyer,
                selected: false,
              })),
            },
          },
        },
      })
    );
  }, [buyerOptionsResult.data]);

  useEffect(() => {
    const rawDistributors = distributorOptionsResult.data?.turnoverinvoice;
    if (!rawDistributors) return;

    const distributorMap = new Map<string, string>();

    rawDistributors.forEach((inv) => {
      const id =
        inv.turnoverinvoice_detail?.matched_distributor_name ?? inv.sellername;
      const label =
        inv.turnoverinvoice_detail?.matched_distributor_name ??
        inv.turnoverinvoice_detail?.distributordetails?.name ??
        inv.sellername;

      if (id && label && !distributorMap.has(id)) {
        distributorMap.set(id, label);
      }
    });

    dispatch(
      updateFiltersByRoute({
        route: FilterRoute.Turnover,
        filter: {
          listFilter: {
            [ListFilterKey.Distributor]: {
              multiSelect: true,
              values: Array.from(distributorMap.entries())
                .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB))
                .map(([id, label]) => ({
                  id,
                  label,
                  selected: false,
                })),
            },
          },
        },
      })
    );
  }, [distributorOptionsResult.data]);

  return {
    invoicesList,
    isLoading: invoicesResult.isLoading && pageNo === 1,
    isLoadingMore: isPageLoading,
    hasMore: !isEndOfList,
    lastItemRef,
    onScroll,
  };
};
